/**
 * Does a product carry the design the seller made?
 *
 * One rule, used by the step-2 "only matching products" filter, the block on
 * products lacking a Position, and the embroidery run of the guided entry.
 * Kept pure and free of UI types so all of them read the same answer.
 *
 * A product matches when, for every Position the design uses, the product has
 * that Position, the Position offers the Decoration Method the design was made
 * for (print or embroidery), and its Print Area is at least as wide as the
 * design needs there. The width check only applies where the carry-over
 * keeps the reference width (the relative rule); wherever the target
 * decides the size (RUNDUM, percent, class change, KLEIN) it is skipped.
 */

import type {
  CarryOverAnchor,
  ProductClass,
} from '../design/carryOverDefaults.js';
import { carryOverRule } from '../design/carryOverRule.js';
import type { SizeTier } from './sizeTier.js';

export type DecorationCategory = 'print' | 'embroidery';

export interface MatchPosition {
  /** Position slug, e.g. `front`, `back`. */
  slug: string;
  /** Physical width of the Print Area; null when the catalogue has none. */
  maxWidthMm: number | null;
  /**
   * Effective size tier of the Print Area. Onto a KLEIN area the carry-over
   * maximises the design instead of keeping the reference width, so the
   * width check does not apply there (R18).
   */
  sizeTier?: SizeTier | null;
  /** Admin marker RUNDUM (a KLEIN override still in the catalogue reads as percent 100). */
  sizeTierOverride?: SizeTier | null;
  /** Effective carry-over settings of the area (area over product). */
  carryOverPercent?: number | null;
  carryOverAnchor?: CarryOverAnchor | null;
  carryOverTopOffsetMm?: number | null;
  /** Default decoration category; null when the Position carries no method. */
  decoration: DecorationCategory | null;
  /** Minimum print resolution the default method asks for, when it states one. */
  minDpi?: number | null;
  /**
   * Every category the Position offers. A Position can be printed AND
   * embroidered; the default alone would then hide the other run. Absent on
   * older product shapes, in which case the default stands in.
   */
  decorations?: readonly DecorationCategory[];
}

export interface MatchProduct {
  /** Product id and class; the rule's identity and class-change inputs. */
  productId?: string | null;
  productClass?: ProductClass | null;
  positions: MatchPosition[];
}

/** What the design needs on one Position. */
export interface PositionRequirement {
  slug: string;
  /** Design width on the reference Print Area; 0 when unknown (no width check). */
  widthMm: number;
  /** Size tier of the reference Print Area the width was measured on, when known. */
  sourceSizeTier?: SizeTier;
  /** The reference (master) product and its class, when known. */
  sourceProductId?: string | null;
  sourceProductClass?: ProductClass | null;
  /** How the motif on this Position is made; print when not stated. */
  decoration?: DecorationCategory;
}

export type MismatchReason =
  /** The product has no Position with that slug. */
  | 'missing-position'
  /** The design is a print, the Position cannot be printed. */
  | 'not-print'
  /** The design is embroidered, the Position cannot be embroidered. */
  | 'not-embroidery'
  /** The Print Area is narrower than the design needs. */
  | 'too-narrow';

export interface ProductMismatch {
  slug: string;
  reason: MismatchReason;
  /** Print Area width for `too-narrow`, so the UI can say by how much. */
  maxWidthMm?: number;
  requiredWidthMm?: number;
}

export interface ProductMatchResult {
  matches: boolean;
  mismatches: ProductMismatch[];
}

/**
 * The categories a Position offers. A Position without any Decoration Method
 * counts as print — the catalogue's conservative default (see
 * `getDecorationCategoryFromCode` in the web app): missing data must not hide
 * print products, and must never promise embroidery.
 */
function offeredDecorations(
  position: MatchPosition
): readonly DecorationCategory[] {
  if (position.decorations && position.decorations.length > 0) {
    return position.decorations;
  }
  return [position.decoration ?? 'print'];
}

/**
 * Whether the carry-over keeps the reference width on this Position: only
 * the relative rule does. Asked of the one rule so the filter never decides
 * on tiers, percents or classes by itself.
 */
function keepsReferenceWidth(
  product: MatchProduct,
  position: MatchPosition,
  requirement: PositionRequirement
): boolean {
  const decision = carryOverRule(
    {
      productId: requirement.sourceProductId ?? undefined,
      productClass: requirement.sourceProductClass ?? undefined,
      sizeTier: requirement.sourceSizeTier,
    },
    {
      productId: product.productId ?? undefined,
      productClass: product.productClass ?? undefined,
      sizeTier: position.sizeTier ?? undefined,
      sizeTierOverride: position.sizeTierOverride ?? undefined,
      carryOverPercent: position.carryOverPercent ?? undefined,
      carryOverAnchor: position.carryOverAnchor ?? undefined,
      carryOverTopOffsetMm: position.carryOverTopOffsetMm ?? undefined,
    },
    { isMaster: false }
  );
  return decision.rule === 'relative';
}

/**
 * Evaluate one product against the design's requirements.
 *
 * A Print Area without a physical width skips the width check rather than
 * failing it: missing data must not hide products. So does every Position
 * where the target decides the size (`keepsReferenceWidth`).
 */
export function matchProduct(
  product: MatchProduct,
  requirements: PositionRequirement[]
): ProductMatchResult {
  const mismatches: ProductMismatch[] = [];

  for (const requirement of requirements) {
    const position = product.positions.find((p) => p.slug === requirement.slug);
    if (!position) {
      mismatches.push({ slug: requirement.slug, reason: 'missing-position' });
      continue;
    }
    const wanted = requirement.decoration ?? 'print';
    if (!offeredDecorations(position).includes(wanted)) {
      mismatches.push({
        slug: requirement.slug,
        reason: wanted === 'print' ? 'not-print' : 'not-embroidery',
      });
      continue;
    }
    if (
      requirement.widthMm > 0 &&
      position.maxWidthMm !== null &&
      position.maxWidthMm > 0 &&
      position.maxWidthMm < requirement.widthMm &&
      // The width check is meaningless where the carry-over does not keep
      // the reference width: such a Position shows the whole design at the
      // size the target decides.
      keepsReferenceWidth(product, position, requirement)
    ) {
      mismatches.push({
        slug: requirement.slug,
        reason: 'too-narrow',
        maxWidthMm: position.maxWidthMm,
        requiredWidthMm: requirement.widthMm,
      });
    }
  }

  return { matches: mismatches.length === 0, mismatches };
}
